/**
 * Léxico del lenguaje de campos calculados en el navegador (mismo que el motor:
 * report_creator_engine/services/formula_parser.py). Sirve para resaltar la fórmula,
 * saber en qué función y argumento está el cursor (tooltip de parámetros), sugerir
 * campos/funciones y detectar agregados sin contar lo que va dentro de {FIXED …}.
 * La validación completa (campos, tipos, número de argumentos) la hace el motor.
 */

export const KEYWORDS = [
    'IF', 'THEN', 'ELSEIF', 'ELSE', 'END', 'CASE', 'WHEN', 'AND', 'OR', 'NOT', 'IN',
    'TRUE', 'FALSE', 'NULL', 'FIXED', 'INCLUDE', 'EXCLUDE'
];
const KEYWORD_SET = new Set(KEYWORDS);
const OPERATORS = ['**', '==', '!=', '<>', '<=', '>=', '&&', '||', '+', '-', '*', '/', '%', '^', '=', '<', '>', '!', '&', '(', ')', ',', ';', ':', '{', '}'];

const AGG_NAMES = {
    SUM: 'sum', SUMA: 'sum',
    AVG: 'mean', PROMEDIO: 'mean', MEAN: 'mean', AVERAGE: 'mean',
    MIN: 'min', MINIMO: 'min',
    MAX: 'max', MAXIMO: 'max',
    COUNT: 'count', CONTEO: 'count', CUENTA: 'count',
    COUNTD: 'count_distinct', COUNT_DISTINCT: 'count_distinct', CONTEO_UNICO: 'count_distinct',
    MEDIAN: 'median', MEDIANA: 'median'
};

/** Función que escribe cada agregación de AGG_OPTIONS (sum → SUM, count_distinct → COUNTD…). */
export const AGG_FN_FOR = {
    sum: 'SUM', mean: 'AVG', median: 'MEDIAN', count: 'COUNT', count_distinct: 'COUNTD', max: 'MAX', min: 'MIN'
};

/** Nombre sin acentos y en mayúsculas (así se buscan funciones y alias). */
export function fnKey(name) {
    return String(name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

const isWordChar = (ch) => /[\p{L}\p{N}_]/u.test(ch || '');
const operatorAt = (src, index) => OPERATORS.find(item => src.startsWith(item, index));

function readQuoted(text, i, closer) {
    let j = i + 1;
    while (j < text.length) {
        const ch = text[j];
        if (ch === '\\' && closer !== ']' && j + 1 < text.length) {
            j += 2;
            continue;
        }
        if (ch === closer) {
            if (text[j + 1] === closer) {
                j += 2;
                continue;
            }
            return { end: j + 1, closed: true };
        }
        j += 1;
    }
    return { end: text.length, closed: false };
}

/**
 * Tokens que cubren todo el texto (incluidos espacios y comentarios) para poder pintarlo.
 * type: ws, comment, str, field, date, num, kw, fn, ident, op, bad.
 */
export function tokenizeFormula(text) {
    const src = String(text || '');
    const tokens = [];
    let i = 0;
    const push = (type, start, end, extra = {}) => tokens.push({ type, start, end, text: src.slice(start, end), ...extra });
    while (i < src.length) {
        const ch = src[i];
        if (/\s/.test(ch)) {
            let j = i + 1;
            while (j < src.length && /\s/.test(src[j])) j += 1;
            push('ws', i, j);
            i = j;
            continue;
        }
        if (src.startsWith('//', i)) {
            const nl = src.indexOf('\n', i);
            const end = nl < 0 ? src.length : nl;
            push('comment', i, end);
            i = end;
            continue;
        }
        if (src.startsWith('/*', i)) {
            const close = src.indexOf('*/', i + 2);
            const end = close < 0 ? src.length : close + 2;
            push('comment', i, end, { closed: close >= 0 });
            i = end;
            continue;
        }
        if (ch === '"' || ch === "'") {
            const { end, closed } = readQuoted(src, i, ch);
            push('str', i, end, { closed });
            i = end;
            continue;
        }
        if (ch === '[' || ch === '`') {
            const { end, closed } = readQuoted(src, i, ch === '[' ? ']' : '`');
            const inner = src.slice(i + 1, closed ? end - 1 : end).replace(/]]/g, ']');
            push('field', i, end, { closed, name: inner });
            i = end;
            continue;
        }
        if (ch === '#') {
            const close = src.indexOf('#', i + 1);
            if (close > i && !src.slice(i + 1, close).includes('\n')) {
                push('date', i, close + 1);
                i = close + 1;
                continue;
            }
        }
        const num = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(i));
        if (num && !isWordChar(src[i + num[0].length])) {
            push('num', i, i + num[0].length);
            i += num[0].length;
            continue;
        }
        if (isWordChar(ch)) {
            let j = i + 1;
            while (j < src.length && isWordChar(src[j])) j += 1;
            const word = src.slice(i, j);
            let k = j;
            while (k < src.length && /\s/.test(src[k])) k += 1;
            const upper = word.toUpperCase();
            if (src[k] === '(' && (!KEYWORD_SET.has(upper) || upper === 'IF')) {
                push('fn', i, j, { name: word });
            } else if (KEYWORD_SET.has(upper)) {
                push('kw', i, j, { name: upper });
            } else {
                push('ident', i, j, { name: word });
            }
            i = j;
            continue;
        }
        const op = operatorAt(src, i);
        if (op) {
            push('op', i, i + op.length, { op: op === ';' ? ',' : op });
            i += op.length;
            continue;
        }
        push('bad', i, i + 1);
        i += 1;
    }
    return tokens;
}

const significant = (tokens) => tokens.filter(t => t.type !== 'ws' && t.type !== 'comment');

/**
 * Llamadas a función del texto con su nombre, argumentos (tokens) y profundidad de llaves.
 * braceDepth > 0 significa que la llamada está dentro de un nivel de detalle {…}.
 */
function listCalls(formula) {
    const toks = significant(tokenizeFormula(formula));
    const calls = [];
    // Un marco por paréntesis abierto: la llamada, o null si es un paréntesis de agrupación.
    const stack = [];
    let braces = 0;
    const addTo = (frames, tok) => frames.forEach(frame => {
        if (frame) frame.args[frame.args.length - 1].push(tok);
    });
    toks.forEach((tok, index) => {
        const op = tok.type === 'op' ? tok.op : null;
        if (op === '{') braces += 1;
        if (op === '}') braces = Math.max(0, braces - 1);
        if (op === '(') {
            addTo(stack, tok);
            const prev = toks[index - 1];
            stack.push(prev && prev.type === 'fn'
                ? { name: prev.name, nameToken: prev, args: [[]], braceDepth: braces }
                : null);
            return;
        }
        if (op === ')') {
            const call = stack.pop();
            if (call) calls.push(call);
            addTo(stack, tok);
            return;
        }
        const top = stack[stack.length - 1];
        if (op === ',' && top) {
            top.args.push([]);
            addTo(stack.slice(0, -1), tok);
            return;
        }
        addTo(stack, tok);
    });
    return calls.sort((a, b) => a.nameToken.start - b.nameToken.start);
}

function aggOf(call) {
    const agg = AGG_NAMES[fnKey(call.name)];
    if (!agg) return null;
    const argCount = call.args.filter(arg => arg.length > 0).length;
    // MIN/MAX con dos argumentos comparan fila por fila; no agregan.
    if ((agg === 'min' || agg === 'max') && argCount > 1) return null;
    return agg;
}

/** true si la fórmula agrega (SUM, AVG, COUNTD…) fuera de toda expresión {FIXED …}. */
export function formulaHasAggregates(formula) {
    if (!formula) return false;
    return listCalls(formula).some(call => call.braceDepth === 0 && aggOf(call));
}

function simpleColumn(argTokens) {
    if (!argTokens.length) return '';
    if (argTokens.length === 1 && argTokens[0].type === 'field') return argTokens[0].name.trim();
    if (argTokens.every(tok => tok.type === 'ident' || tok.type === 'num')) {
        return argTokens.map(tok => tok.text).join(' ').trim();
    }
    return '';
}

/** Agregaciones de una sola columna fuera de LOD: [{agg, column, fn}] en orden de aparición. */
export function extractAggCalls(formula) {
    if (!formula) return [];
    return listCalls(formula)
        .filter(call => call.braceDepth === 0 && aggOf(call))
        .map(call => ({ call, agg: aggOf(call), column: call.args.length === 1 ? simpleColumn(call.args[0]) : '' }))
        .filter(item => item.column)
        .map(({ call, agg, column }) => ({ agg, column, fn: call.name, start: call.nameToken.start, end: call.nameToken.end }));
}

/** Cambia el nombre de la función de la n-ésima agregación simple (misma numeración que extractAggCalls). */
export function replaceNthAggFn(formula, index, nextFn) {
    const target = extractAggCalls(formula)[index];
    if (!target) return formula;
    const text = String(formula || '');
    return `${text.slice(0, target.start)}${nextFn}${text.slice(target.end)}`;
}

/**
 * Función (o bloque IF/CASE/LOD) en la que está el cursor y el argumento activo.
 * Devuelve { name, argIndex } o null.
 */
export function callContextAt(formula, cursor) {
    const tokens = tokenizeFormula(String(formula || '').slice(0, cursor));
    const stack = [];
    const blocks = [];
    let prev = null;
    tokens.forEach(tok => {
        if (tok.type === 'ws' || tok.type === 'comment') return;
        if (tok.type === 'op' && tok.op === '(') {
            stack.push({ name: prev && prev.type === 'fn' ? prev.name : null, argIndex: 0, kind: 'call' });
        } else if (tok.type === 'op' && tok.op === '{') {
            stack.push({ name: 'FIXED', argIndex: 0, kind: 'lod' });
        } else if (tok.type === 'op' && (tok.op === ')' || tok.op === '}')) {
            stack.pop();
        } else if (tok.type === 'op' && tok.op === ',' && stack.length) {
            // Las comas entre dimensiones de un LOD no cambian de parámetro.
            if (stack[stack.length - 1].kind !== 'lod') stack[stack.length - 1].argIndex += 1;
        } else if (tok.type === 'op' && tok.op === ':' && stack.length && stack[stack.length - 1].kind === 'lod') {
            stack[stack.length - 1].argIndex = 1;
        } else if (tok.type === 'kw' && stack.length && stack[stack.length - 1].kind === 'lod'
            && ['FIXED', 'INCLUDE', 'EXCLUDE'].includes(tok.name)) {
            stack[stack.length - 1].name = tok.name;
        } else if (tok.type === 'kw' && (tok.name === 'IF' || tok.name === 'CASE')) {
            blocks.push({ name: tok.name, depth: stack.length, argIndex: 0 });
        } else if (tok.type === 'kw' && blocks.length) {
            // Parámetros del catálogo: IF (condición, valor, otro); CASE (expresión, valor, resultado).
            const block = blocks[blocks.length - 1];
            const isCase = block.name === 'CASE';
            if (tok.name === 'END') blocks.pop();
            else if (tok.name === 'THEN') block.argIndex = isCase ? 2 : 1;
            else if (tok.name === 'ELSE') block.argIndex = 2;
            else if (tok.name === 'ELSEIF') block.argIndex = 0;
            else if (tok.name === 'WHEN') block.argIndex = 1;
        }
        prev = tok;
    });
    for (let i = stack.length - 1; i >= 0; i -= 1) {
        if (stack[i].name) {
            const block = blocks[blocks.length - 1];
            if (block && block.depth > i) return { name: block.name, argIndex: block.argIndex };
            return { name: stack[i].name, argIndex: stack[i].argIndex };
        }
    }
    const block = blocks[blocks.length - 1];
    return block ? { name: block.name, argIndex: block.argIndex } : null;
}

/**
 * Lo que el usuario está escribiendo en el cursor, para el autocompletado.
 * mode: 'field' (dentro de [ … ), 'word' (identificador) o null (en texto o comentario).
 */
export function completionContextAt(formula, cursor) {
    const before = String(formula || '').slice(0, cursor);
    const tokens = tokenizeFormula(before);
    const last = tokens[tokens.length - 1];
    if (!last) return { mode: 'word', query: '', start: cursor };
    if (last.type === 'str' && !last.closed) return null;
    if (last.type === 'comment' && (last.text.startsWith('//') || last.closed === false)) return null;
    if (last.type === 'field' && !last.closed && last.text.startsWith('[')) {
        return { mode: 'field', query: last.text.slice(1), start: last.start };
    }
    if (last.type === 'ident' || last.type === 'kw' || last.type === 'fn') {
        return { mode: 'word', query: last.text, start: last.start };
    }
    return { mode: 'word', query: '', start: cursor };
}

/**
 * Renombra campos dentro de una fórmula: `[Nombre]`, `` `Nombre` `` y nombres escritos sin
 * corchetes (de una o varias palabras), sin importar mayúsculas. No toca textos entre
 * comillas, comentarios ni fechas. Un nombre nuevo que no es identificador simple se
 * escribe entre corchetes.
 * @param {string} formula
 * @param {Record<string, string>} mapping nombre viejo → nombre nuevo
 */
export function renameFormulaFields(formula, mapping = {}) {
    const text = String(formula || '');
    const lookup = new Map(Object.entries(mapping || {}).map(([from, to]) => [String(from).trim().toLowerCase(), to]));
    if (!lookup.size) return text;
    const asRef = (name) => (/^[\p{L}_][\p{L}\p{N}_]*$/u.test(name) && !KEYWORD_SET.has(name.toUpperCase())
        ? name
        : `[${String(name).replace(/]/g, ']]')}]`);
    const toks = tokenizeFormula(text);
    let out = '';
    let t = 0;
    while (t < toks.length) {
        const tok = toks[t];
        if (tok.type === 'field' && lookup.has(tok.name.trim().toLowerCase())) {
            out += `[${String(lookup.get(tok.name.trim().toLowerCase())).replace(/]/g, ']]')}]`;
            t += 1;
            continue;
        }
        if (tok.type === 'ident') {
            /* Racha «palabra (espacio palabra)*»: se prueba la frase más larga primero. */
            const words = [tok.name];
            const ends = [t];
            let k = t;
            while (toks[k + 1]?.type === 'ws' && toks[k + 2]?.type === 'ident') {
                words.push(toks[k + 2].name);
                ends.push(k + 2);
                k += 2;
            }
            let matched = false;
            for (let n = words.length; n >= 1; n -= 1) {
                const phrase = words.slice(0, n).join(' ').toLowerCase();
                if (lookup.has(phrase)) {
                    out += asRef(lookup.get(phrase));
                    t = ends[n - 1] + 1;
                    matched = true;
                    break;
                }
            }
            if (matched) continue;
        }
        out += tok.text;
        t += 1;
    }
    return out;
}

/** Campos citados (entre corchetes, backticks o sin comillas) que están en knownNames. */
export function formulaFieldRefs(formula, knownNames = []) {
    const toks = tokenizeFormula(formula);
    const written = new Set();
    toks.forEach(tok => {
        if (tok.type === 'field') written.add(tok.name.trim().toLowerCase());
        if (tok.type === 'ident') written.add(tok.text.toLowerCase());
    });
    const text = String(formula || '');
    return knownNames.filter(name => {
        if (written.has(String(name).toLowerCase())) return true;
        if (!/\s|[^\p{L}\p{N}_]/u.test(name)) return false;
        const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|[^\\p{L}\\p{N}_\`])${escaped}([^\\p{L}\\p{N}_\`]|$)`, 'u').test(text);
    });
}

/** Texto para insertar un campo: siempre entre corchetes (los ] del nombre se duplican). */
export function fieldReference(name) {
    return `[${String(name || '').replace(/]/g, ']]')}]`;
}

/**
 * Plantilla de inserción de una entrada del catálogo y posición del cursor dentro de ella.
 * Las funciones quedan como NOMBRE( para que aparezca el tooltip de parámetros.
 */
export function insertionFor(entry) {
    if (!entry) return { text: '', caret: 0 };
    if (entry.kind === 'syntax' && entry.insert) {
        const text = entry.insert;
        const marks = [text.indexOf('[]'), text.indexOf('  '), text.indexOf('()')].filter(pos => pos >= 0);
        const caret = marks.length ? Math.min(...marks) + 1 : text.length;
        return { text, caret };
    }
    const text = `${entry.name}(`;
    return { text, caret: text.length };
}

/** Índice del catálogo por nombre y alias (sin acentos, mayúsculas). */
export function indexCatalog(catalog) {
    const byKey = {};
    (catalog?.functions || []).forEach(entry => {
        [entry.name, ...(entry.aliases || [])].forEach(name => { byKey[fnKey(name)] = entry; });
    });
    return byKey;
}

/** Firma legible de una entrada, p. ej. CONTAINS(texto, buscar). */
export function signatureParts(entry) {
    if (!entry) return { head: '', params: [] };
    const params = (entry.params || []).map(param => ({
        ...param,
        label: `${param.name}${param.variadic ? '…' : ''}`
    }));
    return { head: entry.name, params, syntax: entry.syntax || '' };
}

export const TYPE_LABEL = {
    number: 'Número',
    string: 'Texto',
    date: 'Fecha',
    boolean: 'Verdadero/Falso',
    any: 'Mixto'
};
